import { formatDate } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, LOCALE_ID } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { NetworkStatusService } from "../../services/network-status.service";
import { ChipsComponent } from "../../modules/chips/chips.component";
import { TranslatePipe } from "../../pipes/translate.pipe";
import { TranslateUtils } from "../../utils/translate.utils";

export type NetworkBadgeState = "offline" | "back" | "slow";

/**
 * INDICATEUR DU RÉSEAU (01/10/2026) — monté UNE fois, à la racine (`app.component.html`) : il est
 * là aussi sur la page de connexion. Une pastille en haut, au centre (sur la barre de recherche,
 * inutile hors ligne), qui ne masque ni les boutons de l'en-tête ni ceux de l'écran :
 *  - **Pas de réseau** / **Serveur injoignable** (`role="alert"`) — « Informations de 10:42 · rien
 *    ne peut être enregistré pour l'instant » + « Réessayer » (`btn-network-retry`, une sonde) ;
 *  - **Réseau revenu** (4 s) ;
 *  - **Réseau lent** — une requête attend depuis plus de 8 s.
 * Rien quand tout va bien. L'écran garde ce qu'il affichait : on dit seulement de quand ça date.
 * État : `NetworkStatusService` ; testid `network-status`, `data-state` = offline | back | slow.
 */
@Component({
  selector: "vl-network-status",
  standalone: true,
  imports: [MatIconModule, ChipsComponent, TranslatePipe],
  templateUrl: "./network-status.component.html",
  styleUrl: "./network-status.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetworkStatusComponent {
  network = inject(NetworkStatusService);
  private locale = inject(LOCALE_ID);

  state = computed<NetworkBadgeState | null>(() => {
    if (!this.network.online()) return "offline";
    if (this.network.justBack()) return "back";
    return this.network.slow() ? "slow" : null;
  });

  icon = computed(() => ({ offline: this.network.reason() === "server" ? "cloud_off" : "wifi_off", back: "wifi", slow: "network_check" })[this.state() ?? "slow"]);

  title = computed(() => {
    const s = this.state();
    if (s === "offline") return this.network.reason() === "server" ? "system.network.server_title" : "system.network.offline_title";
    return s === "back" ? "system.network.back_title" : "system.network.slow_title";
  });

  detail = computed(() => {
    const s = this.state();
    if (s === "back") return TranslateUtils.translate("system.network.back_text");
    if (s === "slow") return TranslateUtils.translate("system.network.slow_text");
    const at = this.network.lastContact();
    if (at == null) return TranslateUtils.translate("system.network.offline_never");
    const sameDay = new Date(at).toDateString() === new Date().toDateString();
    return TranslateUtils.translate("system.network.offline_since", {
      heure: formatDate(at, sameDay ? "HH:mm" : "dd/MM HH:mm", this.locale),
    });
  });

  retry(): void {
    void this.network.probeNow();
  }
}
